import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { CsvExportControl } from '../../components/common/CsvExportControl';
import { DashboardEmailFilter } from '../../components/common/DashboardEmailFilter';
import { DashboardImageControls } from '../../components/common/DashboardImageControls';
import { DashboardImagePreview } from '../../components/common/DashboardImagePreview';
import { MasterLabelLink } from '../../components/common/MasterLabelLink';
import { ReviewStatusControl } from '../../components/common/ReviewStatusControl';
import { useDashboardImageSettings } from '../../hooks/useDashboardImageSettings';
import {
  chainFilterParam,
  enumParam,
  nullableStringParam,
  pageParam,
  stringListParam,
  stringParam,
  useUrlFilterState,
} from '../../hooks/useUrlFilterState';
import { ZoomMeta } from '../../hooks/useZoomPreview';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  ForgeDesignGroupDashboardRow,
  ForgeDesignGroupHierarchyLink,
  ForgeDesignGroupsChainSnapshot,
  ForgeDesignGroupScope,
  ForgeDesignGroupsDashboardResponse,
  ForgeDesignGroupSummary,
  ForgeReviewSort,
} from '../../types';
import { CsvValue, downloadCsv, fetchPagedRows } from '../../utils/csvExport';

const PAGE_LIMIT = 50;
const CSV_EXPORT_PAGE_LIMIT = 200;

const DESIGN_GROUPS_CSV_HEADERS: string[] = [
  'group_id',
  'chain_id',
  'chain_name',
  'scope',
  'design_count',
  'design_ids',
  'hidden_design_count',
  'parent_group_ids',
  'child_group_ids',
  'is_reviewed',
  'reviewed_by',
  'reviewed_at',
  'master_label_url',
  'created_at',
  'updated_at',
  'updated_by',
];

function designGroupsCsvRow(group: ForgeDesignGroupDashboardRow): CsvValue[] {
  return [
    group.groupId,
    group.chainId,
    group.chainName,
    group.scope,
    group.designCount,
    group.designIds.join(' '),
    group.hiddenDesignCount,
    group.parentGroups.map((link) => link.groupId).join(' '),
    group.childGroups.map((link) => link.groupId).join(' '),
    group.isReviewed,
    group.reviewedBy,
    group.reviewedAt,
    group.masterLabelUrl?.startsWith('/')
      ? `${window.location.origin}${group.masterLabelUrl}`
      : group.masterLabelUrl,
    group.createdAt,
    group.updatedAt,
    group.updatedBy,
  ];
}
const SCOPE_OPTIONS: Array<{ value: ForgeDesignGroupScope | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'full', label: 'Full' },
  { value: 'partial', label: 'Partial' },
  { value: 'non-front', label: 'Non-front' },
];
const GROUP_SCOPE_OPTIONS = SCOPE_OPTIONS.filter(
  (option): option is { value: ForgeDesignGroupScope; label: string } => option.value !== 'all'
);

const DESIGN_GROUPS_URL_FILTERS = {
  chain: chainFilterParam(),
  scope: enumParam(['full', 'partial', 'non-front'] as const, 'all'),
  q: stringParam(),
  updatedBy: stringListParam(),
  sort: enumParam(
    ['newest', 'unreviewed_first', 'reviewed_first', 'barcode_design_count_desc'] as const,
    'newest'
  ),
  page: pageParam(),
  group: nullableStringParam(),
};

function formatNumber(value?: number | null): string {
  return (value ?? 0).toLocaleString();
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function shortId(value: string): string {
  return value.length > 12 ? `${value.slice(0, 6)}...${value.slice(-4)}` : value;
}

function scopeLabel(scope: ForgeDesignGroupScope): string {
  if (scope === 'full') return 'Full';
  if (scope === 'partial') return 'Partial';
  return 'Non-front';
}

function scopeClass(scope: ForgeDesignGroupScope): string {
  if (scope === 'full') return 'is-full';
  if (scope === 'partial') return 'is-partial';
  return 'is-non-front';
}

function visibleRange(page: number, limit: number, total: number): string {
  if (total === 0) return '0';
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return `${formatNumber(start)}-${formatNumber(end)}`;
}

function isNonEmptyString(value: string | null | undefined): value is string {
  return Boolean(value);
}

type DashboardImageHandlers = Pick<
  ReturnType<typeof useDashboardImageSettings>,
  'handleImageHover' | 'handleImageMove' | 'handleImageLeave'
>;

function groupDesignZoomMeta(design: ForgeDesignGroupSummary): ZoomMeta {
  return {
    metadataType: 'design',
    id: design.designId,
    normalizedBarcode: design.normalizedBarcode ?? undefined,
    barcodeStatus: design.barcodeStatus ? String(design.barcodeStatus) : undefined,
    cropCount: design.cropCount ?? undefined,
    noteCount: design.noteCount ?? undefined,
    createdAt: design.createdAt ?? undefined,
    updatedAt: design.updatedAt ?? undefined,
    updatedBy: design.updatedBy ?? undefined,
  };
}

function renderGroupMemberBody(
  design: ForgeDesignGroupDashboardRow['designs'][number],
  imageSize: number,
  imageHandlers: DashboardImageHandlers
) {
  return (
    <>
      <DashboardImagePreview
        imageUrl={design.previewVizUrl}
        fallback={shortId(design.designId)}
        size={Math.max(28, Math.round(imageSize * 0.86))}
        title={design.normalizedBarcode ?? design.designId}
        meta={groupDesignZoomMeta(design)}
        onImageHover={imageHandlers.handleImageHover}
        onImageMove={imageHandlers.handleImageMove}
        onImageLeave={imageHandlers.handleImageLeave}
      />
      <span>
        <strong>{design.normalizedBarcode ?? shortId(design.designId)}</strong>
        <em>{shortId(design.designId)}</em>
      </span>
    </>
  );
}

type DesignGroupsDashboardUserOptionAliases = ForgeDesignGroupsDashboardResponse & {
  updated_by_options?: string[];
};

function updatedByOptionsFromGroups(
  dashboard: ForgeDesignGroupsDashboardResponse | null
): string[] {
  const snakeOptions = (dashboard as DesignGroupsDashboardUserOptionAliases | null)
    ?.updated_by_options;
  const rowOptions =
    dashboard?.groups.map((group) => group.updatedBy).filter(isNonEmptyString) ?? [];
  return [
    ...new Set([...(dashboard?.updatedByOptions ?? []), ...(snakeOptions ?? []), ...rowOptions]),
  ].sort();
}

function mergeChainSnapshots(
  base: ForgeDesignGroupsChainSnapshot[],
  updates: ForgeDesignGroupsChainSnapshot[]
): ForgeDesignGroupsChainSnapshot[] {
  const updatesById = new Map(updates.map((chain) => [chain.chain.chainId, chain]));
  const baseIds = new Set(base.map((chain) => chain.chain.chainId));
  return [
    ...base.map((chain) => updatesById.get(chain.chain.chainId) ?? chain),
    ...updates.filter((chain) => !baseIds.has(chain.chain.chainId)),
  ].sort((left, right) => left.chain.chainId - right.chain.chainId);
}

function mergeChainRefresh(
  current: ForgeDesignGroupsDashboardResponse,
  refreshed: ForgeDesignGroupsDashboardResponse,
  replaceGroups: boolean
): ForgeDesignGroupsDashboardResponse {
  const chains = mergeChainSnapshots(current.chains, refreshed.chains);

  return {
    ...current,
    computedAtUtc: refreshed.computedAtUtc,
    supportedChainIds: refreshed.supportedChainIds,
    chains,
    groups: replaceGroups ? refreshed.groups : current.groups,
    total: replaceGroups ? refreshed.total : current.total,
    page: replaceGroups ? refreshed.page : current.page,
    limit: replaceGroups ? refreshed.limit : current.limit,
    hasNext: replaceGroups ? refreshed.hasNext : current.hasNext,
  };
}

interface ScopePillProps {
  scope: ForgeDesignGroupScope;
}

const ScopePill: React.FC<ScopePillProps> = ({ scope }) => (
  <span className={`dg-scope-pill ${scopeClass(scope)}`}>{scopeLabel(scope)}</span>
);

const ReviewPill: React.FC<{ isReviewed: boolean }> = ({ isReviewed }) => (
  <span className={`dg-scope-pill ${isReviewed ? 'is-reviewed' : 'is-unreviewed'}`}>
    {isReviewed ? 'Reviewed' : 'Needs review'}
  </span>
);

interface GroupThumbsProps {
  designs: ForgeDesignGroupSummary[];
  hiddenCount: number;
  imageSize: number;
  imageHandlers: DashboardImageHandlers;
}

const GroupThumbs: React.FC<GroupThumbsProps> = ({
  designs,
  hiddenCount,
  imageSize,
  imageHandlers,
}) => (
  <div className="dg-thumb-strip" aria-label="Design previews">
    {designs.slice(0, 8).map((design) => (
      <DashboardImagePreview
        key={design.designId}
        className="dg-thumb"
        imageUrl={design.previewVizUrl}
        fallback={shortId(design.designId)}
        size={imageSize}
        title={design.normalizedBarcode ?? design.designId}
        meta={groupDesignZoomMeta(design)}
        onImageHover={imageHandlers.handleImageHover}
        onImageMove={imageHandlers.handleImageMove}
        onImageLeave={imageHandlers.handleImageLeave}
      />
    ))}
    {hiddenCount > 0 ? (
      <div className="dg-thumb dg-thumb-more" style={{ width: imageSize, height: imageSize }}>
        +{formatNumber(hiddenCount)}
      </div>
    ) : null}
  </div>
);

interface ChainCardProps {
  chain: ForgeDesignGroupsChainSnapshot;
  isSelected: boolean;
  isRefreshing: boolean;
  onSelect: () => void;
  onRefresh: () => void;
}

const ChainCard: React.FC<ChainCardProps> = ({
  chain,
  isSelected,
  isRefreshing,
  onSelect,
  onRefresh,
}) => (
  <article className={`dg-chain-card ${isSelected ? 'is-selected' : ''}`}>
    <button type="button" className="dg-chain-select" onClick={onSelect}>
      <span className="dg-chain-title">{chain.chain.chainName}</span>
      <span className="dg-chain-subtitle">chain {chain.chain.chainId}</span>
      <span className="dg-chain-totals">
        <span className="dg-chain-stat">
          <strong>{formatNumber(chain.uniqueDesignCount)}</strong>
          designs
        </span>
        <span className="dg-chain-stat">
          <strong>{formatNumber(chain.groupCount)}</strong>
          groups
        </span>
      </span>
    </button>
    <div className="dg-chain-bars">
      <span
        className="dg-chain-bar is-full"
        style={{ '--value': chain.scopeTotals.full } as React.CSSProperties}
      >
        {formatNumber(chain.scopeTotals.full)}
      </span>
      <span
        className="dg-chain-bar is-partial"
        style={{ '--value': chain.scopeTotals.partial } as React.CSSProperties}
      >
        {formatNumber(chain.scopeTotals.partial)}
      </span>
      <span
        className="dg-chain-bar is-non-front"
        style={{ '--value': chain.scopeTotals.nonFront } as React.CSSProperties}
      >
        {formatNumber(chain.scopeTotals.nonFront)}
      </span>
    </div>
    <button
      type="button"
      className="dg-icon-button"
      aria-label={`Refresh ${chain.chain.chainName}`}
      title={`Refresh ${chain.chain.chainName}`}
      disabled={isRefreshing}
      onClick={onRefresh}
    >
      <RefreshOutlinedIcon className={isRefreshing ? 'is-spinning' : ''} />
    </button>
  </article>
);

interface GroupRowProps {
  row: ForgeDesignGroupDashboardRow;
  isSelected: boolean;
  isTypeMenuOpen: boolean;
  isChangingType: boolean;
  isReviewing: boolean;
  canReview: boolean;
  imageSize: number;
  imageHandlers: DashboardImageHandlers;
  onSelect: () => void;
  onOpenTypeMenu: () => void;
  onChangeScope: (scope: ForgeDesignGroupScope) => void;
  onToggleReview: () => void;
}

interface ScopeChangeNotice {
  fromScope: ForgeDesignGroupScope;
  toScope: ForgeDesignGroupScope;
}

const GroupRow: React.FC<GroupRowProps> = ({
  row,
  isSelected,
  isTypeMenuOpen,
  isChangingType,
  isReviewing,
  canReview,
  imageSize,
  imageHandlers,
  onSelect,
  onOpenTypeMenu,
  onChangeScope,
  onToggleReview,
}) => (
  <tr
    className={`dg-group-row ${isSelected ? 'is-selected' : ''}`}
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    }}
  >
    <td className="dg-row-reviewed-cell">
      <ReviewStatusControl
        isReviewed={row.isReviewed}
        isBusy={isReviewing}
        canToggle={canReview}
        reviewedLabel="Clear group review"
        unreviewedLabel="Mark group reviewed"
        reviewedBy={row.reviewedBy}
        reviewedAt={row.reviewedAt}
        testId={`design-group-review-button-${row.groupId}`}
        onToggle={(event) => {
          event.stopPropagation();
          onToggleReview();
        }}
      />
    </td>
    <td className="dg-row-id" title={row.groupId}>
      {row.masterLabelUrl ? (
        <MasterLabelLink
          className="dg-id-link"
          to={row.masterLabelUrl}
          title={`Open group ${row.groupId} in Master Label`}
          onClick={(event) => event.stopPropagation()}
        >
          {shortId(row.groupId)}
        </MasterLabelLink>
      ) : (
        shortId(row.groupId)
      )}
    </td>
    <td className="dg-row-type">
      <div className="dg-type-cell-actions">
        <ScopePill scope={row.scope} />
        <button
          type="button"
          className="dg-type-button"
          aria-expanded={isTypeMenuOpen}
          aria-label={`Change group type for ${shortId(row.groupId)}`}
          disabled={isChangingType}
          onClick={(event) => {
            event.stopPropagation();
            onOpenTypeMenu();
          }}
        >
          <TuneOutlinedIcon className={isChangingType ? 'is-spinning' : ''} />
          Type
        </button>
        {isTypeMenuOpen ? (
          <div
            className="dg-type-menu"
            role="menu"
            aria-label={`Group type options for ${shortId(row.groupId)}`}
          >
            {GROUP_SCOPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="menuitem"
                disabled={row.scope === option.value || isChangingType}
                onClick={(event) => {
                  event.stopPropagation();
                  onChangeScope(option.value);
                }}
              >
                <ScopePill scope={option.value} />
                {row.scope === option.value ? 'Current' : `Make ${option.label}`}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </td>
    <td className="dg-row-count">{formatNumber(row.designCount)} designs</td>
    <td className="dg-row-examples">
      <GroupThumbs
        designs={row.designs}
        hiddenCount={row.hiddenDesignCount}
        imageSize={imageSize}
        imageHandlers={imageHandlers}
      />
    </td>
    <td className="dg-row-hierarchy">
      {row.parentGroups.length + row.childGroups.length > 0
        ? `${row.parentGroups.length} up / ${row.childGroups.length} down`
        : 'No linked scopes'}
    </td>
    <td className="dg-row-timestamp">{formatTimestamp(row.createdAt)}</td>
    <td className="dg-row-timestamp">{formatTimestamp(row.updatedAt)}</td>
    <td className="dg-row-user" title={row.updatedBy ?? undefined}>
      {row.updatedBy ?? '-'}
    </td>
  </tr>
);

interface HierarchyLinkButtonProps {
  link: ForgeDesignGroupHierarchyLink;
  onOpen: (groupId: string) => void;
}

const HierarchyLinkButton: React.FC<HierarchyLinkButtonProps> = ({ link, onOpen }) => (
  <button type="button" className="dg-tree-node" onClick={() => onOpen(link.groupId)}>
    <ScopePill scope={link.scope} />
    <span>{scopeLabel(link.scope)} group</span>
    <strong>{formatNumber(link.designCount)}</strong>
    <em>{formatNumber(link.sharedDesignCount)} shared</em>
  </button>
);

interface HierarchyPanelProps {
  row: ForgeDesignGroupDashboardRow;
  imageSize: number;
  imageHandlers: DashboardImageHandlers;
  onOpenGroup: (groupId: string, chainId: number) => void;
}

const HierarchyPanel: React.FC<HierarchyPanelProps> = ({
  row,
  imageSize,
  imageHandlers,
  onOpenGroup,
}) => (
  <div className="dg-inline-panel dg-hierarchy-panel">
    <div className="dg-panel-title">
      <AccountTreeOutlinedIcon />
      <span className="dg-panel-title-text" title={`Hierarchy for ${row.groupId}`}>
        Hierarchy for {row.groupId}
      </span>
    </div>
    <div className="dg-selected-summary">
      <ScopePill scope={row.scope} />
      <span>{formatNumber(row.designCount)} designs</span>
    </div>
    <div className="dg-selected-summary">
      <span>{row.isReviewed ? 'Reviewed by' : 'Review'}</span>
      <ReviewPill isReviewed={row.isReviewed} />
      <strong title={row.reviewedAt ?? undefined}>
        {row.isReviewed ? row.reviewedBy || 'reviewed' : 'pending'}
      </strong>
    </div>
    <div className="dg-tree">
      <div className="dg-tree-section">
        <div className="dg-tree-label">Stronger</div>
        {row.parentGroups.length > 0 ? (
          row.parentGroups.map((link) => (
            <HierarchyLinkButton
              key={link.groupId}
              link={link}
              onOpen={(groupId) => onOpenGroup(groupId, row.chainId)}
            />
          ))
        ) : (
          <div className="dg-tree-empty">None</div>
        )}
      </div>
      <div className="dg-tree-current">
        <ScopePill scope={row.scope} />
        <span>Selected group</span>
      </div>
      <div className="dg-tree-section">
        <div className="dg-tree-label">Weaker</div>
        {row.childGroups.length > 0 ? (
          row.childGroups.map((link) => (
            <HierarchyLinkButton
              key={link.groupId}
              link={link}
              onOpen={(groupId) => onOpenGroup(groupId, row.chainId)}
            />
          ))
        ) : (
          <div className="dg-tree-empty">None</div>
        )}
      </div>
    </div>
    <div className="dg-panel-title dg-panel-title--secondary">Members</div>
    <div className="dg-member-list">
      {row.designs.map((design) => {
        const memberUrl = design.forgeUrl ?? row.masterLabelUrl ?? null;
        return memberUrl ? (
          <MasterLabelLink key={design.designId} className="dg-member" to={memberUrl}>
            {renderGroupMemberBody(design, imageSize, imageHandlers)}
          </MasterLabelLink>
        ) : (
          <div key={design.designId} className="dg-member">
            {renderGroupMemberBody(design, imageSize, imageHandlers)}
          </div>
        );
      })}
      {row.hiddenDesignCount > 0 ? (
        <div className="dg-member-overflow">
          +{formatNumber(row.hiddenDesignCount)} more designs
        </div>
      ) : null}
    </div>
  </div>
);

export const DesignGroupsDashboard: React.FC = () => {
  const { token, canDoTasks } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [dashboard, setDashboard] = useState<ForgeDesignGroupsDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filters, updateFilters] = useUrlFilterState(DESIGN_GROUPS_URL_FILTERS);
  const {
    chain: selectedChainId,
    scope: selectedScope,
    q: committedQuery,
    updatedBy: selectedUpdatedBy,
    sort: reviewSort,
    page,
    group: selectedGroupId,
  } = filters;
  const [queryInput, setQueryInput] = useState(committedQuery);
  const [refreshingChainIds, setRefreshingChainIds] = useState<Set<number>>(() => new Set());
  const [allChains, setAllChains] = useState<ForgeDesignGroupsChainSnapshot[] | null>(null);

  useEffect(() => {
    setQueryInput(committedQuery);
  }, [committedQuery]);
  const [typeMenuGroupId, setTypeMenuGroupId] = useState<string | null>(null);
  const [changingTypeGroupId, setChangingTypeGroupId] = useState<string | null>(null);
  const [reviewingGroupIds, setReviewingGroupIds] = useState<Set<string>>(() => new Set());
  const [scopeChangeNotice, setScopeChangeNotice] = useState<ScopeChangeNotice | null>(null);
  const loadRequestSequence = useRef(0);
  const imageSettings = useDashboardImageSettings('forge-design-groups-dashboard-crop-size', 42);

  const loadDashboard = useCallback(async () => {
    const requestSequence = loadRequestSequence.current + 1;
    loadRequestSequence.current = requestSequence;
    setIsLoading(true);
    try {
      const nextDashboard = await ForgeApiService.fetchDesignGroupsDashboard(token, {
        chainId: selectedChainId === 'all' ? null : selectedChainId,
        scope: selectedScope === 'all' ? null : selectedScope,
        query: committedQuery || null,
        updatedByValues: selectedUpdatedBy,
        reviewSort,
        page,
        limit: PAGE_LIMIT,
      });
      if (loadRequestSequence.current !== requestSequence) return;
      setDashboard(nextDashboard);
      if (selectedChainId === 'all') {
        setAllChains(nextDashboard.chains);
      }
      setLoadError(null);
    } catch (error) {
      if (loadRequestSequence.current !== requestSequence) return;
      const message = error instanceof Error ? error.message : 'Error loading Design Groups';
      setDashboard(null);
      setLoadError(message);
      showErrorToast(message);
    } finally {
      if (loadRequestSequence.current === requestSequence) {
        setIsLoading(false);
      }
    }
  }, [
    committedQuery,
    page,
    reviewSort,
    selectedChainId,
    selectedScope,
    selectedUpdatedBy,
    showErrorToast,
    token,
  ]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  // Chain cards always show every supported chain; when the page opens with a
  // chain filter already in the URL, the filtered response only carries that
  // chain, so fetch the full chain list once to seed the cards.
  const seededAllChainsRef = useRef(false);
  useEffect(() => {
    if (seededAllChainsRef.current) return;
    seededAllChainsRef.current = true;
    if (selectedChainId === 'all') return;
    ForgeApiService.fetchDesignGroupsDashboard(token, {
      chainId: null,
      scope: null,
      query: null,
      updatedByValues: [],
      reviewSort: 'newest',
      page: 1,
      limit: 1,
    })
      .then((full) => setAllChains((current) => current ?? full.chains))
      .catch(() => {
        // Supplementary fetch; the cards fall back to the filtered chain only.
      });
  }, [selectedChainId, token]);

  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const handleExportCsv = useCallback(
    async (exportLimit: number) => {
      if (isExportingCsv) return;
      setIsExportingCsv(true);
      try {
        const rows = await fetchPagedRows<ForgeDesignGroupDashboardRow>(
          async (exportPage, pageSize) => {
            const response = await ForgeApiService.fetchDesignGroupsDashboard(token, {
              chainId: selectedChainId === 'all' ? null : selectedChainId,
              scope: selectedScope === 'all' ? null : selectedScope,
              query: committedQuery || null,
              updatedByValues: selectedUpdatedBy,
              reviewSort,
              page: exportPage,
              limit: pageSize,
            });
            return { rows: response.groups, total: response.total };
          },
          { limit: exportLimit, pageSize: CSV_EXPORT_PAGE_LIMIT }
        );
        const chainSuffix = selectedChainId === 'all' ? '' : `-chain-${selectedChainId}`;
        downloadCsv(
          `forge-design-groups${chainSuffix}.csv`,
          DESIGN_GROUPS_CSV_HEADERS,
          rows.map(designGroupsCsvRow)
        );
      } catch {
        showErrorToast('Failed to export design groups CSV');
      } finally {
        setIsExportingCsv(false);
      }
    },
    [
      isExportingCsv,
      token,
      selectedChainId,
      selectedScope,
      committedQuery,
      selectedUpdatedBy,
      reviewSort,
      showErrorToast,
    ]
  );

  const reloadCurrentDashboardSlice = async () => {
    const requestSequence = loadRequestSequence.current + 1;
    loadRequestSequence.current = requestSequence;
    const nextDashboard = await ForgeApiService.fetchDesignGroupsDashboard(token, {
      chainId: selectedChainId === 'all' ? null : selectedChainId,
      scope: selectedScope === 'all' ? null : selectedScope,
      query: committedQuery || null,
      updatedByValues: selectedUpdatedBy,
      reviewSort,
      page,
      limit: PAGE_LIMIT,
    });
    if (loadRequestSequence.current !== requestSequence) return;
    setDashboard(nextDashboard);
    if (selectedChainId === 'all') {
      setAllChains(nextDashboard.chains);
    }
    setLoadError(null);
  };

  useEffect(() => {
    if (!dashboard) {
      return;
    }
    if (selectedGroupId && !dashboard.groups.some((group) => group.groupId === selectedGroupId)) {
      updateFilters({ group: null });
    }
    if (typeMenuGroupId && !dashboard.groups.some((group) => group.groupId === typeMenuGroupId)) {
      setTypeMenuGroupId(null);
    }
  }, [dashboard, selectedGroupId, typeMenuGroupId, updateFilters]);

  const chainOptions = useMemo(
    () => mergeChainSnapshots(allChains ?? [], dashboard?.chains ?? []),
    [allChains, dashboard?.chains]
  );
  const updatedByOptions = useMemo(() => updatedByOptionsFromGroups(dashboard), [dashboard]);

  const refreshRequest = (chainId: number) => ({
    chain_id: chainId,
    scope: selectedScope === 'all' ? null : selectedScope,
    query: committedQuery || null,
    updated_by_values: selectedUpdatedBy,
    review_sort: reviewSort,
    page,
    limit: PAGE_LIMIT,
  });

  const dashboardSliceRequest = () => ({
    dashboard_chain_id: selectedChainId === 'all' ? null : selectedChainId,
    dashboard_scope: selectedScope === 'all' ? null : selectedScope,
    query: committedQuery || null,
    updated_by_values: selectedUpdatedBy,
    review_sort: reviewSort,
    page,
    limit: PAGE_LIMIT,
  });

  const handleRefreshChain = async (chainId: number) => {
    setRefreshingChainIds((current) => new Set(current).add(chainId));
    try {
      const refreshed = await ForgeApiService.refreshDesignGroupsDashboard(
        token,
        refreshRequest(chainId)
      );
      setDashboard((current) =>
        current ? mergeChainRefresh(current, refreshed, selectedChainId === chainId) : refreshed
      );
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error refreshing Design Groups');
    } finally {
      setRefreshingChainIds((current) => {
        const next = new Set(current);
        next.delete(chainId);
        return next;
      });
    }
  };

  const handleChangeGroupScope = async (
    row: ForgeDesignGroupDashboardRow,
    nextScope: ForgeDesignGroupScope
  ) => {
    if (row.scope === nextScope) {
      setTypeMenuGroupId(null);
      return;
    }
    setChangingTypeGroupId(row.groupId);
    try {
      const refreshed = await ForgeApiService.updateDesignGroupScope(token, row.groupId, {
        scope: nextScope,
        ...dashboardSliceRequest(),
      });
      setDashboard(refreshed);
      const changedGroupStillVisible = refreshed.groups.some(
        (group) => group.groupId === row.groupId
      );
      updateFilters({
        group: changedGroupStillVisible ? row.groupId : (refreshed.groups[0]?.groupId ?? null),
      });
      setTypeMenuGroupId(null);
      setScopeChangeNotice(
        !changedGroupStillVisible && selectedScope !== 'all' && selectedScope !== nextScope
          ? { fromScope: row.scope, toScope: nextScope }
          : null
      );
      showSuccessToast(`Group changed to ${scopeLabel(nextScope)}`);
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error changing Design Group type');
    } finally {
      setChangingTypeGroupId(null);
    }
  };

  const handleToggleGroupReview = async (row: ForgeDesignGroupDashboardRow) => {
    if (!canDoTasks) return;
    const nextIsReviewed = !row.isReviewed;
    setReviewingGroupIds((current) => new Set(current).add(row.groupId));
    try {
      await ForgeApiService.updateDesignGroupReviewStatus(token, row.groupId, {
        is_reviewed: nextIsReviewed,
      });
      await reloadCurrentDashboardSlice();
      showSuccessToast(
        nextIsReviewed ? 'Design Group marked reviewed' : 'Design Group review cleared'
      );
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Error updating Design Group review status'
      );
    } finally {
      setReviewingGroupIds((current) => {
        const next = new Set(current);
        next.delete(row.groupId);
        return next;
      });
    }
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setScopeChangeNotice(null);
    updateFilters({ q: queryInput.trim(), page: 1 });
  };

  const handleOpenHierarchyGroup = (groupId: string, chainId: number) => {
    setQueryInput(groupId);
    updateFilters({
      chain: chainId,
      scope: 'all',
      q: groupId,
      updatedBy: [],
      sort: 'newest',
      page: 1,
    });
  };

  const handleSelectChain = (chainId: number | 'all') => {
    setTypeMenuGroupId(null);
    setScopeChangeNotice(null);
    updateFilters({ chain: chainId, page: 1 });
  };

  const handleSelectScope = (scope: ForgeDesignGroupScope | 'all') => {
    setTypeMenuGroupId(null);
    setScopeChangeNotice(null);
    updateFilters({ scope, page: 1 });
  };

  const clearSearch = () => {
    setQueryInput('');
    setTypeMenuGroupId(null);
    setScopeChangeNotice(null);
    updateFilters({ q: '', updatedBy: [], sort: 'newest', page: 1 });
  };

  const handleSelectUpdatedBy = (nextSelected: string[]) => {
    updateFilters({ updatedBy: nextSelected, page: 1 });
  };

  const handleReviewSortChange = (nextSort: ForgeReviewSort) => {
    updateFilters({ sort: nextSort, page: 1 });
  };

  const hasListFilters =
    Boolean(committedQuery) || selectedUpdatedBy.length > 0 || reviewSort !== 'newest';

  return (
    <div className="page-content page-content--wide dg-dashboard-page">
      <section className="forge-card dg-dashboard-hero">
        <div className="dg-hero-bar">
          <div>
            <div className="dg-dashboard-title">Design Groups</div>
            <div className="rdc-dashboard-copy">
              Design group hierarchy, scope review, and membership checks across supported chains.
            </div>
          </div>
          <div className="dg-hero-controls">
            <div className="dg-scope-tabs" role="group" aria-label="Design group scope">
              {SCOPE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={selectedScope === option.value ? 'is-active' : ''}
                  onClick={() => handleSelectScope(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <DashboardImageControls
              label="Design Groups"
              cropSize={imageSettings.cropSize}
              onCropSizeChange={imageSettings.setCropSize}
              zoomEnabled={imageSettings.zoomEnabled}
              onToggleZoom={imageSettings.toggleZoom}
            />
          </div>
        </div>
      </section>

      <section className="dg-chain-grid" aria-label="Design Groups chain stats">
        {chainOptions.map((chain) => (
          <ChainCard
            key={chain.chain.chainId}
            chain={chain}
            isSelected={selectedChainId === chain.chain.chainId}
            isRefreshing={refreshingChainIds.has(chain.chain.chainId)}
            onSelect={() =>
              handleSelectChain(
                selectedChainId === chain.chain.chainId ? 'all' : chain.chain.chainId
              )
            }
            onRefresh={() => void handleRefreshChain(chain.chain.chainId)}
          />
        ))}
      </section>

      <section className="dg-dashboard-grid">
        <div className="forge-card dg-list-panel">
          <div className="dg-list-header">
            <div>
              <div className="dg-panel-title">Groups</div>
              <div className="dg-list-count">
                Showing{' '}
                {visibleRange(
                  dashboard?.page ?? page,
                  dashboard?.limit ?? PAGE_LIMIT,
                  dashboard?.total ?? 0
                )}{' '}
                of {formatNumber(dashboard?.total ?? 0)}
              </div>
            </div>
            <div className="dg-pagination">
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Previous page"
                disabled={page <= 1 || isLoading}
                onClick={() => updateFilters({ page: Math.max(1, page - 1) })}
              >
                <ChevronLeftOutlinedIcon />
              </button>
              <span>Page {formatNumber(page)}</span>
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Next page"
                disabled={!dashboard?.hasNext || isLoading}
                onClick={() => updateFilters({ page: page + 1 })}
              >
                <ChevronRightOutlinedIcon />
              </button>
            </div>
            <form className="dg-list-controls" onSubmit={handleSearchSubmit}>
              <div className="dg-search dg-search--compact">
                <SearchOutlinedIcon />
                <input
                  type="search"
                  aria-label="Search Design Groups"
                  placeholder="Group ID, design ID, or barcode"
                  value={queryInput}
                  onChange={(event) => setQueryInput(event.target.value)}
                />
              </div>
              <DashboardEmailFilter
                ariaLabel="Filter Design Groups by updated user"
                options={updatedByOptions}
                selected={selectedUpdatedBy}
                onChange={handleSelectUpdatedBy}
              />
              <label className="dg-sort-control">
                <span>Sort</span>
                <select
                  aria-label="Sort Design Groups by review status"
                  value={reviewSort}
                  onChange={(event) =>
                    handleReviewSortChange(event.target.value as ForgeReviewSort)
                  }
                >
                  <option value="newest">Newest</option>
                  <option value="unreviewed_first">Not reviewed first</option>
                  <option value="reviewed_first">Reviewed first</option>
                </select>
              </label>
              <CsvExportControl
                total={dashboard?.total ?? 0}
                exporting={isExportingCsv}
                buttonClassName="dg-search-clear"
                onExport={(limit) => {
                  void handleExportCsv(limit);
                }}
              />
              {hasListFilters ? (
                <button type="button" className="dg-search-clear" onClick={clearSearch}>
                  Clear
                </button>
              ) : null}
              <button type="submit" className="dg-primary-button">
                Search
              </button>
            </form>
          </div>
          {scopeChangeNotice ? (
            <div className="dg-scope-change-notice" role="status">
              <span>
                Changed from {scopeLabel(scopeChangeNotice.fromScope)} to{' '}
                {scopeLabel(scopeChangeNotice.toScope)}. It is hidden by the current{' '}
                {scopeLabel(scopeChangeNotice.fromScope)} filter.
              </span>
              <button
                type="button"
                className="dg-notice-button"
                onClick={() => handleSelectScope(scopeChangeNotice.toScope)}
              >
                Show {scopeLabel(scopeChangeNotice.toScope)}
              </button>
              <button
                type="button"
                className="dg-notice-dismiss"
                aria-label="Dismiss group type change notice"
                onClick={() => setScopeChangeNotice(null)}
              >
                Dismiss
              </button>
            </div>
          ) : null}
          <div className="dg-group-list">
            {isLoading ? (
              <div className="dg-loading-state">Loading Design Groups...</div>
            ) : loadError ? (
              <div className="dg-empty-state">
                <strong>Design Groups could not be loaded</strong>
                <span>{loadError}</span>
                <button
                  type="button"
                  className="dg-secondary-button"
                  onClick={() => void loadDashboard()}
                >
                  Retry
                </button>
              </div>
            ) : dashboard?.groups.length ? (
              <div className="dg-group-table-scroll">
                <table className="dg-group-table" aria-label="Design Groups table">
                  <thead>
                    <tr>
                      <th>Reviewed</th>
                      <th>Group</th>
                      <th>Type</th>
                      <th>Designs</th>
                      <th>Examples</th>
                      <th>Hierarchy</th>
                      <th>Created</th>
                      <th>Updated</th>
                      <th>Updated by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.groups.map((row) => (
                      <React.Fragment key={row.groupId}>
                        <GroupRow
                          row={row}
                          isSelected={row.groupId === selectedGroupId}
                          isTypeMenuOpen={row.groupId === typeMenuGroupId}
                          isChangingType={row.groupId === changingTypeGroupId}
                          isReviewing={reviewingGroupIds.has(row.groupId)}
                          canReview={canDoTasks}
                          imageSize={imageSettings.cropSize}
                          imageHandlers={imageSettings}
                          onSelect={() =>
                            updateFilters({
                              group: row.groupId === selectedGroupId ? null : row.groupId,
                            })
                          }
                          onOpenTypeMenu={() =>
                            setTypeMenuGroupId((current) =>
                              current === row.groupId ? null : row.groupId
                            )
                          }
                          onChangeScope={(scope) => void handleChangeGroupScope(row, scope)}
                          onToggleReview={() => void handleToggleGroupReview(row)}
                        />
                        {row.groupId === selectedGroupId ? (
                          <tr className="dg-inline-detail-row">
                            <td colSpan={9}>
                              <HierarchyPanel
                                row={row}
                                imageSize={imageSettings.cropSize}
                                imageHandlers={imageSettings}
                                onOpenGroup={handleOpenHierarchyGroup}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dg-empty-state">No Design Groups match this view.</div>
            )}
          </div>
        </div>
      </section>
      {imageSettings.ZoomOverlay}
    </div>
  );
};
